import { useEffect, useState, type ReactNode } from 'react';

import { PAGE_GATE_WAIT_MS, RefusedRedirect } from '@/auth/PageGate';
import { useSession } from '@/auth/SessionProvider';
import { type SettingsGateName, useSettingsGate } from '@/auth/settingsGates';

/**
 * Route gate of a Settings page (auth/settingsGates.ts, the same rules as the
 * Settings menu): the page mounts, and fires its requests, only when the gate
 * admits the active role; otherwise /nopermission (the shared no-permission
 * page, as PageGate and CapabilityGate do; the role's home on its first
 * landing, PageGate's `RefusedRedirect`).
 *
 * Like PageGate it decides only once the session holds the active role's
 * permission map (fail closed after PAGE_GATE_WAIT_MS), and shows `fallback`
 * while the map or the gate's one identity check is pending.
 */
export function SettingsGate({
  gate,
  children,
  fallback = null,
}: {
  gate: SettingsGateName;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { permissionsLoaded: loaded } = useSession();
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (loaded) return undefined;
    const timer = setTimeout(() => setTimedOut(true), PAGE_GATE_WAIT_MS);
    return () => clearTimeout(timer);
  }, [loaded]);

  const open = useSettingsGate(gate, loaded || timedOut);
  if (open === null) return <>{fallback}</>;
  return open ? <>{children}</> : <RefusedRedirect />;
}
